import { Component, OnDestroy, OnInit } from '@angular/core';
import { TotalsService } from 'app/services/totals.service';
import { FormBuilder, FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { ISelectOption } from '../../../models/filter-form.models';
import { DepartmentService } from '../../../services/department.service';
import { BehaviorSubject, Subject, takeUntil } from 'rxjs';
import { TotalFilter } from '../../../models/filter.models';
import { Totals } from '../../../models/totals.models';
import { atLeastOneValidator } from '../../../utils/form.utils';
import { AuthService } from '../../../services/auth.service';
import moment from 'moment/moment';
import { AsyncPipe, NgFor, NgIf } from '@angular/common';
import { MatError, MatFormField, MatHint, MatLabel, MatSuffix } from '@angular/material/form-field';
import { MatSelect } from '@angular/material/select';
import { MatOption } from '@angular/material/autocomplete';
import {
  MatDatepickerToggle,
  MatDateRangeInput,
  MatDateRangePicker,
  MatEndDate,
  MatStartDate,
} from '@angular/material/datepicker';
import { MatButton } from '@angular/material/button';
import { MatProgressSpinner } from '@angular/material/progress-spinner';
import { CardComponent } from '../../shared/card/card.component';

@Component({
  selector: 'app-totals',
  templateUrl: './totals.component.html',
  styleUrls: ['./totals.component.scss'],
  imports: [
    FormsModule,
    ReactiveFormsModule,
    NgIf,
    MatFormField,
    MatSelect,
    NgFor,
    MatOption,
    MatLabel,
    MatDateRangeInput,
    MatStartDate,
    MatEndDate,
    MatHint,
    MatDatepickerToggle,
    MatSuffix,
    MatDateRangePicker,
    MatError,
    MatButton,
    MatProgressSpinner,
    CardComponent,
    AsyncPipe,
  ],
})
export class TotalsComponent implements OnInit, OnDestroy {
  private readonly destroy$: Subject<void> = new Subject<void>();

  readonly loading$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
  readonly totalsFilterForm: FormGroup = this.fb.group(
    {
      departmentId: new FormControl(''),
      fromDate: new FormControl(''),
      toDate: new FormControl(''),
    },
    { validators: atLeastOneValidator },
  );

  totalsItems: Array<Totals> = [];
  departmentOptions: ISelectOption[] = [];

  get isDepartmentDisabled(): boolean {
    return this.authService.currentRole.includes('ROLE_TD_ADMIN');
  }

  constructor(
    private readonly totalsService: TotalsService,
    private readonly fb: FormBuilder,
    private readonly departmentService: DepartmentService,
    private readonly authService: AuthService,
  ) {}

  ngOnInit(): void {
    this.departmentService
      .getDepartments()
      .pipe(takeUntil(this.destroy$))
      .subscribe((departments) => {
        this.departmentOptions = departments.map(({ id, name }) => ({
          value: id,
          viewValue: name,
        }));
      });

    this.loadData();
  }

  search(): void {
    const formValues = this.totalsFilterForm.value;
    const transformedValues = { ...formValues };

    if (transformedValues.fromDate) {
      transformedValues.fromDate = moment(transformedValues.fromDate).format('YYYY-MM-DD');
    } else {
      delete transformedValues.fromDate;
    }

    if (!transformedValues.departmentId) {
      delete transformedValues.departmentId;
    }

    if (transformedValues.toDate) {
      transformedValues.toDate = moment(transformedValues.toDate).format('YYYY-MM-DD');
    } else {
      delete transformedValues.toDate;
    }

    this.loadData(transformedValues);
  }

  loadData(filter?: TotalFilter): void {
    this.loading$.next(true);
    this.totalsService
      .getTotals(filter)
      .pipe(takeUntil(this.destroy$))
      .subscribe((response) => {
        this.totalsItems = response;
        this.loading$.next(false);
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  clear(): void {
    this.totalsFilterForm.reset();
    this.loadData();
  }
}
